import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, RefreshCw, Send, SwitchCamera, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { isMobileContainer } from '../../../utils/device';
import { compressImageForChat } from '../../../utils/crmChatImageCompress';

export default function CrmChatCameraModal({ open, onOpenChange, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [activeDeviceId, setActiveDeviceId] = useState('');
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const isMobile = isMobileContainer();

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async (deviceId) => {
    stopStream();
    setError('');
    try {
      const constraints = {
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : { facingMode: isMobile ? 'environment' : 'user' },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      setError('Camera permission denied or unavailable.');
    }
  }, [isMobile, stopStream]);

  const loadDevices = useCallback(async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const cams = list.filter((d) => d.kind === 'videoinput');
      setDevices(cams);
      if (cams.length && !activeDeviceId) {
        setActiveDeviceId(cams[0].deviceId);
      }
    } catch {
      // ignore
    }
  }, [activeDeviceId]);

  useEffect(() => {
    if (!open) {
      stopStream();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewBlob(null);
      setPreviewUrl(null);
      setError('');
      return undefined;
    }

    if (isMobile) return undefined;

    loadDevices().then(() => {
      if (activeDeviceId) startCamera(activeDeviceId);
      else startCamera('');
    });

    return () => stopStream();
  }, [open, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || isMobile || !activeDeviceId) return;
    startCamera(activeDeviceId);
  }, [activeDeviceId, open, isMobile, startCamera]);

  const captureFromVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
      stopStream();
    }, 'image/jpeg', 0.9);
  };

  const handleNativeCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewBlob(file);
    setPreviewUrl(URL.createObjectURL(file));
    e.target.value = '';
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewBlob(null);
    setPreviewUrl(null);
    if (!isMobile) startCamera(activeDeviceId || '');
  };

  const handleSend = async () => {
    if (!previewBlob) return;
    setSending(true);
    try {
      const file = previewBlob instanceof File
        ? previewBlob
        : new File([previewBlob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const compressed = await compressImageForChat(file);
      await onCapture?.(compressed, new Date().toISOString());
      onOpenChange(false);
    } finally {
      setSending(false);
    }
  };

  const switchCamera = () => {
    if (devices.length < 2) return;
    const idx = devices.findIndex((d) => d.deviceId === activeDeviceId);
    const next = devices[(idx + 1) % devices.length];
    setActiveDeviceId(next.deviceId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent overlayClassName="z-[80]" className="z-[80] max-w-lg p-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-base">Camera</DialogTitle>
        </DialogHeader>

        {error && (
          <div className="px-4 text-sm text-destructive">{error}</div>
        )}

        <div className="relative bg-black aspect-[4/3] flex items-center justify-center">
          {previewUrl ? (
            <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
          ) : isMobile ? (
            <div className="text-center text-white/80 p-6">
              <Camera className="h-12 w-12 mx-auto mb-3 opacity-70" />
              <p className="text-sm mb-4">Open your device camera to capture a photo.</p>
              <Button type="button" onClick={() => fileInputRef.current?.click()}>
                Open Camera
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleNativeCapture}
              />
            </div>
          ) : (
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-border">
          {previewUrl ? (
            <>
              <Button type="button" variant="outline" onClick={retake} disabled={sending}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Retake
              </Button>
              <Button type="button" onClick={handleSend} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700">
                <Send className="h-4 w-4 mr-1" />
                Send
              </Button>
            </>
          ) : isMobile ? (
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          ) : (
            <>
              <div className="flex gap-1">
                {devices.length > 1 && (
                  <Button type="button" variant="outline" size="icon" onClick={switchCamera} aria-label="Switch camera">
                    <SwitchCamera className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <Button type="button" onClick={captureFromVideo} className="bg-emerald-600 hover:bg-emerald-700">
                <Camera className="h-4 w-4 mr-1" />
                Capture
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
